import React from "react";
import { motion } from "framer-motion";
import CountUp from "@/components/site/CountUp";
import { TIDS } from "@/lib/testIds";

const stats = [
  { slug: "patients", value: "18,400+", label: "New patient bookings generated", desc: "Across our full client roster in the last 24 months." },
  { slug: "roas", value: "6.4×", label: "Average return on ad spend", desc: "Tracked from ad-click to booked appointment." },
  { slug: "practices", value: "140+", label: "Practices scaled", desc: "Solo dentists, group DSOs, ortho, cosmetic, implants." },
  { slug: "retention", value: "94%", label: "Client retention rate", desc: "We only keep clients we can keep winning for." },
];

const DASH_IMG =
  "https://images.pexels.com/photos/106344/pexels-photo-106344.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Stats() {
  return (
    <section id="results" className="py-24 md:py-32 relative">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <div className="font-mono-accent text-[11px] uppercase tracking-[0.25em] text-blue-400 mb-4">
              / 02 · Proof, not promises
            </div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-medium text-white leading-[1.05]">
              Numbers our clients{" "}
              <span className="italic font-light text-white/60">actually</span>{" "}
              take home.
            </h2>
            <p className="mt-6 text-lg text-white/60 leading-relaxed">
              We report on booked appointments and revenue, not impressions.
              If we can&apos;t move the number that pays your rent, we don&apos;t belong
              in your calendar.
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden border border-white/10">
              <img
                src={DASH_IMG}
                alt="Analytics dashboard on tablet"
                className="w-full h-64 object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#070B14] via-[#070B14]/40 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6">
                <div className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-amber-400 mb-1">
                  Live client dashboard
                </div>
                <div className="font-display text-xl text-white">
                  Weekly booked-appointment reports, every Monday, 8am.
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {stats.map((s, i) => (
              <motion.div
                key={s.slug}
                data-testid={TIDS.statCard(s.slug)}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className={`relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-8 hover:border-amber-400/40 hover:-translate-y-1 transition-[transform,border-color] duration-300 ${
                  i % 3 === 0 ? "sm:translate-y-6" : ""
                }`}
              >
                <div className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-white/50">
                  0{i + 1}
                </div>
                <div className="mt-4 font-display text-5xl md:text-6xl font-semibold tracking-tightest text-white">
                  <CountUp value={s.value} />
                </div>
                <div className="mt-3 text-sm font-medium text-amber-400">
                  {s.label}
                </div>
                <div className="mt-2 text-sm text-white/55 leading-relaxed">
                  {s.desc}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
